import React, { useMemo, useState } from 'react'

const formatDate = (value) => {
  if (!value) {
    return '-'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return '-'
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const formatLabel = (value) => {
  if (!value || value === '-') {
    return '-'
  }

  if (typeof value !== 'string') {
    return value
  }

  return value
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const toStatusSlug = (value) => {
  if (!value || value === '-') {
    return 'enrolled'
  }

  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-')
}

const getInitials = (participant) => {
  const first = participant.first_name ? participant.first_name.charAt(0) : ''
  const last = participant.last_name ? participant.last_name.charAt(0) : ''
  const initials = `${first}${last}`.trim()
  return initials || '?'
}

const toSortString = (value) => {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  return String(value).toLowerCase()
}

const toSortDate = (value) => {
  if (!value) {
    return 0
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? 0 : value.getTime()
  }

  let date = null
  if (typeof value === 'string') {
    const normalized = value.trim()
    const isoMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/)
    const slashMatch = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)

    if (isoMatch) {
      date = new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]))
    } else if (slashMatch) {
      date = new Date(Number(slashMatch[3]), Number(slashMatch[1]) - 1, Number(slashMatch[2]))
    } else {
      date = new Date(normalized)
    }
  } else {
    date = new Date(value)
  }

  return Number.isNaN(date.getTime()) ? 0 : date.getTime()
}

export default function RosterParticipantsGrid({ participants = [], onSelectParticipant }) {
  const [query, setQuery] = useState('')
  const [sortKey, setSortKey] = useState('name')
  const [sortDirection, setSortDirection] = useState('asc')

  const columns = [
    { key: 'status', label: 'Status' },
    { key: 'name', label: 'Athlete' },
    { key: 'group', label: 'Group' },
    { key: 'gender', label: 'Gender' },
    { key: 'dob', label: 'DOB' },
    { key: 'registered', label: 'Registered' },
    { key: 'plan', label: 'Price Plan' },
    { key: 'notes', label: 'Notes' },
  ]

  const rows = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    const filtered = normalized
      ? participants.filter((participant) => {
          const haystack = [
            participant.first_name,
            participant.last_name,
            participant.gender,
            participant.DOB,
            participant.registration_date,
            participant.create_date,
            participant.transaction_date,
            participant.group,
            participant.plan_name,
            participant.plan && participant.plan.plan_name,
            participant.status,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
          return haystack.includes(normalized)
        })
      : participants

    const getSortValue = (participant) => {
      switch (sortKey) {
        case 'gender':
          return toSortString(participant.gender || '-')
        case 'status':
          return toSortString(participant.status || 'enrolled')
        case 'group':
          return toSortString(participant.group || 'Unassigned')
        case 'dob':
          return toSortDate(participant.DOB)
        case 'registered':
          return toSortDate(
            participant.registration_date ||
            participant.create_date ||
            participant.transaction_date
          )
        case 'plan':
          return toSortString(participant.plan_name || (participant.plan && participant.plan.plan_name) || '')
        case 'notes':
          return toSortString(participant.notes || '')
        case 'name':
        default:
          return toSortString(`${participant.last_name || ''} ${participant.first_name || ''}`)
      }
    }

    return [...filtered].sort((a, b) => {
      const aValue = getSortValue(a)
      const bValue = getSortValue(b)

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1
      }
      return 0
    })
  }, [participants, query, sortDirection, sortKey])

  const handleSort = (columnKey) => {
    setSortKey((currentKey) => {
      if (currentKey === columnKey) {
        setSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'))
        return currentKey
      }

      setSortDirection('asc')
      return columnKey
    })
  }

  const renderSortLabel = (columnKey, label) => {
    const isActive = sortKey === columnKey
    const directionLabel = sortDirection === 'asc' ? 'ascending' : 'descending'

    return (
      <button
        type="button"
        className="roster-sort-button"
        onClick={() => handleSort(columnKey)}
        aria-label={`Sort by ${label}${isActive ? `, ${directionLabel}` : ''}`}
        aria-pressed={isActive}
      >
        <span>{label}</span>
        <span
          className={`roster-sort-indicator${isActive ? ' active' : ''}${isActive && sortDirection === 'desc' ? ' desc' : ''}`}
          aria-hidden="true"
        >
          <span className="roster-sort-caret" />
        </span>
      </button>
    )
  }

  return (
    <div className="roster-participants-panel">
      <div className="roster-participants-header">
        <div>
          <div className="roster-participants-kicker">Participants</div>
          <h3>All participants in this program</h3>
        </div>
        <div className="roster-participants-header-actions">
          <input
            className="roster-participants-search"
            type="search"
            placeholder="Search participants"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="roster-participants-count">
            {rows.length} {rows.length === 1 ? 'participant' : 'participants'}
          </div>
        </div>
      </div>

      <div className="roster-participants-table" role="table" aria-label="Participants">
        <div className="roster-participants-table-head" role="row">
          {columns.map((column) => (
            <div role="columnheader" key={column.key}>
              {renderSortLabel(column.key, column.label)}
            </div>
          ))}
        </div>

        <div className="roster-participants-table-body">
          {rows.length ? rows.map((participant, index) => {
            const name = `${participant.first_name || ''} ${participant.last_name || ''}`.trim() || 'Unnamed participant'
            const status = formatLabel(participant.status || '')
            const statusSlug = toStatusSlug(participant.status || status)
            const group = participant.group || 'Unassigned'
            const gender = formatLabel(participant.gender || '-')
            const planName = participant.plan_name || (participant.plan && participant.plan.plan_name) || 'No plan info'
            const registrationDate = formatDate(
              participant.registration_date ||
              participant.create_date ||
              participant.transaction_date
            )
            const notes = participant.notes || '-'
            const hasNotes = notes !== '-'
            const notesTitle = hasNotes ? notes : 'No notes'
            const unreadMessageCount = participant.message_summary && participant.message_summary.unread_admin_count ? participant.message_summary.unread_admin_count : 0

            return (
              <button
                type="button"
                key={`${participant._id || participant.id || participant.email || name || 'participant'}-${index}`}
                className="roster-participant-row"
                onClick={() => onSelectParticipant(participant, participant.club_status)}
              >
                <div className="roster-participant-cell roster-participant-status-cell">
                  <span className={`roster-status-badge roster-status-${statusSlug}`}>{status === '-' ? 'Enrolled' : status}</span>
                  {unreadMessageCount > 0 ? <span className="roster-message-count-pill">{unreadMessageCount} unread</span> : null}
                </div>
                <div className="roster-participant-cell roster-participant-name-cell">
                  <div className="roster-participant-avatar">{getInitials(participant)}</div>
                  <div className="roster-participant-name-wrap">
                    <div className="roster-participant-name">{name}</div>
                  </div>
                </div>
                <div className="roster-participant-cell">{group}</div>
                <div className="roster-participant-cell">{gender}</div>
                <div className="roster-participant-cell">{formatDate(participant.DOB)}</div>
                <div className="roster-participant-cell">{registrationDate}</div>
                <div className="roster-participant-cell roster-participant-plan">{planName}</div>
                <div className="roster-participant-cell roster-participant-notes" title={notesTitle}>
                  <span>{notes}</span>
                </div>
              </button>
            )
          }) : (
            <div className="roster-participants-empty">
              No participants were returned for this program yet.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
